const CHECKOUT_SAFE_PATHS = [
  /^\/checkout(?:\/|$)/,
  /^\/payment-plans(?:\/|$)/,
  /^\/resale\/success(?:\/|$)/
];

const PURCHASE_CHOICE_PATHS = [
  /^\/events\/[^/]+(?:\/|$)/
];

export const isEmbedConsentContext = (location = {}) => {
  const params = new URLSearchParams(location.search || '');
  return params.get('embed') === '1' || params.get('lvm_embed') === '1';
};

export const isEmbedReturnCandidate = (location = {}) => {
  const params = new URLSearchParams(location.search || '');
  return location.pathname === '/checkout/success'
    && Boolean(String(params.get('lvm_embed_token') || '').trim());
};

// A token is only an opaque lookup key. Authority begins after the server has
// verified its HMAC binding, and only on the exact success route.
export const isEmbeddedPurchaseReturn = (
  location = {},
  { verifiedEmbedReturn = false } = {}
) => verifiedEmbedReturn === true && isEmbedReturnCandidate(location);

export const suppressUnsolicitedConsentBanner = (
  location = {},
  { verifiedEmbedReturn = false } = {}
) => (
  isEmbedConsentContext(location) ||
  isEmbeddedPurchaseReturn(location, { verifiedEmbedReturn }) ||
  CHECKOUT_SAFE_PATHS.some((pattern) => pattern.test(location.pathname || '/'))
);

export const isPurchaseConsentRoute = (
  location = {},
  { verifiedEmbedReturn = false } = {}
) => (
  !isEmbedConsentContext(location)
  && !isEmbeddedPurchaseReturn(location, { verifiedEmbedReturn })
  && PURCHASE_CHOICE_PATHS.some((pattern) => pattern.test(location.pathname || '/'))
);

export const resolveSuccessPageConsent = ({
  verifiedEmbedReturn = false,
  snapshotMarketing,
  snapshotStatus,
  snapshotBasis,
  localConsent
} = {}) => {
  if (verifiedEmbedReturn) {
    if (localConsent?.withdrawn === true) {
      return {
        effectiveConsent: localConsent,
        fireBrowserPurchase: false, suppressBanner: true, postWithdrawal: true
      };
    }
    const marketing = snapshotMarketing === true && localConsent?.withdrawn !== true;
    return {
      effectiveConsent: {
        version: 1,
        status: snapshotStatus === 'notice' && snapshotBasis === 'notice_opt_out' ? 'notice' : 'decided',
        ...(snapshotStatus === 'notice' && snapshotBasis === 'notice_opt_out' ? { basis: 'notice_opt_out' } : {}),
        necessary: true,
        analytics: false,
        marketing,
        decisionId: 'verified_order_snapshot',
        decidedAt: null,
        expiresAt: null,
        source: 'embed_order_snapshot',
        authority: 'order_snapshot',
        verifiedEmbedReturn: true,
        browserTracking: marketing
      },
      fireBrowserPurchase: marketing,
      suppressBanner: true,
      postWithdrawal: false
    };
  }

  const effectiveConsent = localConsent || {
    version: 1,
    status: 'pending',
    necessary: true,
    analytics: false,
    marketing: false
  };
  const isDecided = effectiveConsent.status === 'decided'
    || (effectiveConsent.status === 'notice' && effectiveConsent.basis === 'notice_opt_out');
  const marketing = isDecided && effectiveConsent.marketing === true;
  return {
    effectiveConsent,
    fireBrowserPurchase: marketing && effectiveConsent.browserTracking !== false,
    suppressBanner: false,
    postWithdrawal: isDecided && !marketing
  };
};

// Compatibility helper for any remaining callers while the success page is
// migrated to the full resolver. A raw token can never make this true.
export const shouldReplayEmbedReturnPurchase = ({
  location = {},
  verifiedEmbedReturn = false,
  orderConsentMarketing = false
} = {}) => (
  isEmbeddedPurchaseReturn(location, { verifiedEmbedReturn })
  && orderConsentMarketing === true
);
